import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { PurchasingService } from '../../services/purchasing.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { Tpl3plService } from '../../services/tpl3pl.service';

@Component({
	selector: 'app-rga-return',
	templateUrl: './rga-return.component.html',
	styleUrls: ['./rga-return.component.scss']
})
export class RgaReturnComponent implements OnInit {

	@ViewChild('itemCodeRef') itemCodeRef: ElementRef;

	itemScanForm: UntypedFormGroup;
	itemCode: UntypedFormControl = new UntypedFormControl('');

	rgaDetails: any = null;
	rgaHeader: any = null;
	rgaLines: any = [];
	mustship: any = [];
	user: any = [];
	color = 'blue';

	receivingorder: any = false;
	loadingRGA = false;
	loadingdetails = false;

	showError = false;
	errorMessage = '';
	searchsubscription: any = false;

	// PGS 360 RMA — only shown when 3PL integration is configured and enabled (PGS type)
	tpl3plPgsEnabled = false;
	pgsSending = false;
	pgsResult: any = null;

	constructor(
		public location: Location,
		private route: ActivatedRoute,
		public router: Router,
		public formBuilder: UntypedFormBuilder,
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private tpl3plService: Tpl3plService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.globalSearchService.user.subscribe((result) => {
			this.user = result;
		});

		this.itemScanForm = this.formBuilder.group({
			keywords: ['', Validators.required],
		});

		this.route.params.subscribe(params => {
			if (params['id']) {
				this.receivingorder = params['id'];
				this.loadRgaDetails(params['id']);
			}
		});

		// Check if PGS 360 3PL integration is configured — gates the RMA send section
		this.tpl3plService.getConfig().subscribe({
			next: (cfg: any) => {
				this.tpl3plPgsEnabled = !!(cfg?.enabled && cfg?.integration_type === 'PGS');
			},
			error: () => { this.tpl3plPgsEnabled = false; }
		});
	}

	getRgaTypeLabel(rgaType: string): string {
		const typeMap: { [key: string]: string } = {
			'RT': 'Return to Vendor',
			'DG': 'Damaged Goods',
			'OR': 'Other Return',
			'': 'Unspecified'
		};
		return typeMap[rgaType] || 'Unspecified';
	}

	loadRgaDetails(orderno: any) {
		this.loadingRGA = true;
		this.loadingdetails = true;

		this.purchasingService.getRga(orderno).subscribe((result: any) => {
			this.rgaDetails = result;
			this.rgaHeader = result.poheader;
			this.rgaLines = result.polines || [];

			// Process lines to include bin information and calculate pending quantities
			this.mustship = this.rgaLines.map((line: any) => {
				const quantitypending = parseFloat(line.quantityord) - parseFloat(line.quantityrecd || 0);

				// Get bins for this item from the line data
				// The getRgaDetails method already joins with locstock, so we should have bin info
				const bins = this.processBinsForItem(line);

				return {
					...line,
					stockid: line.itemcode,
					description: line.itemdescription,
					quantitypending: quantitypending,
					bins: bins,
					current_movement: 0,
					totalqoh: this.calculateTotalQOH(bins)
				};
			});

			this.loadingRGA = false;
			this.loadingdetails = false;
		}, (error) => {
			this.loadingRGA = false;
			this.loadingdetails = false;
			this.globalSearchService.showNotification('Error loading RGA details', 'danger', 'bottom', 'right');
		});
	}

	processBinsForItem(line: any): any[] {
		let bins: any[] = [];

		// If bins array is already in the data structure, use it
		if (line.bins && Array.isArray(line.bins) && line.bins.length > 0) {
			// Filter to only include bins with quantity > 0
			bins = line.bins
				.filter((bin: any) => parseFloat(bin.quantity || 0) > 0)
				.map((bin: any) => ({
					bin: bin.bin || '',
					loccode: bin.loccode || line.intostocklocation || '',
					quantity: parseFloat(bin.quantity || 0),
					qty: 0 // quantity to ship (user input)
				}));
		} else if (line.bin && line.loccode && line.quantity !== undefined) {
			// If single bin data exists, only include if it has stock
			const qty = parseFloat(line.quantity || 0);
			if (qty > 0) {
				bins = [{
					bin: line.bin,
					loccode: line.loccode,
					quantity: qty,
					qty: 0 // quantity to ship (user input)
				}];
			}
		}

		// Sort by quantity descending (most stock first)
		bins.sort((a, b) => b.quantity - a.quantity);

		// If no bins with stock, return empty array (user can add bins manually via addBin button)
		return bins;
	}

	calculateTotalQOH(bins: any[]): number {
		return bins.reduce((total, bin) => total + parseFloat(bin.quantity || 0), 0);
	}

	itemScan() {
		if (this.searchsubscription) {
			this.searchsubscription.unsubscribe();
		}

		const scanValue = this.itemCode.value;
		if (!scanValue) {return;}

		// Find item by stockid
		const item = this.mustship.find((i: any) => i.stockid === scanValue || i.itemcode === scanValue);

		if (item) {
			// Auto-increment quantity for the first bin
			if (item.bins && item.bins.length > 0) {
				const firstBin = item.bins[0];
				const currentQty = parseFloat(firstBin.qty || 0);
				const newQty = currentQty + 1;

				// Check if we're shipping more than available
				if (newQty > parseFloat(firstBin.quantity || 0)) {
					this.showError = true;
					this.errorMessage = 'Insufficient stock in bin';
					return;
				}

				// Check if we're shipping more than pending
				const totalShipped = this.calculateTotalShipped(item);
				if (totalShipped + 1 > item.quantitypending) {
					this.showError = true;
					this.errorMessage = 'Cannot ship more than pending quantity';
					return;
				}

				firstBin.qty = newQty;
				item.current_movement = 1;
				this.updateItemShippedQuantity(item);
			}

			this.itemCode.setValue('');
			this.showError = false;
		} else {
			this.showError = true;
			this.errorMessage = 'Item not found in RGA';
		}
	}

	calculateTotalShipped(item: any): number {
		return item.bins.reduce((total: number, bin: any) => total + parseFloat(bin.qty || 0), 0);
	}

	updateItemShippedQuantity(item: any) {
		const totalShipped = this.calculateTotalShipped(item);
		// This is for display purposes - the actual quantityrecd will be updated on the server
	}

	selectBin(item: any, bin: any, qty: number) {
		bin.qty = qty;
		this.updateItemShippedQuantity(item);
		this.validateStock(item, bin);
	}

	validateStock(item: any, bin?: any) {
		if (bin) {
			// Validate single bin
			if (parseFloat(bin.qty || 0) > parseFloat(bin.quantity || 0)) {
				this.showError = true;
				this.errorMessage = `Insufficient stock in bin ${bin.bin}. Available: ${bin.quantity}`;
				return false;
			}
		}

		// Validate total shipped doesn't exceed pending
		const totalShipped = this.calculateTotalShipped(item);
		if (totalShipped > item.quantitypending) {
			this.showError = true;
			this.errorMessage = `Cannot ship more than pending quantity (${item.quantitypending})`;
			return false;
		}

		this.showError = false;
		return true;
	}

	setBin(value: any, bin: any) {
		bin.bin = value.toUpperCase();
	}

	addBin(item: any) {
		const item_bin = {
			stockid: item.stockid,
			bin: '',
			loccode: item.intostocklocation || item.loccode || '',
			quantity: 0,
			qty: 0
		};

		item.bins.push(item_bin);
	}

	removeBin(bin: any, item: any) {
		let proceed = true;
		if (bin.qty > 0) {
			proceed = confirm('This bin has quantity to ship. Remove bin?');
		}

		if (proceed) {
			if (bin.qty > 0) {
				item.current_movement = -1 * bin.qty;
				bin.qty = 0;
				this.updateItemShippedQuantity(item);
			}

			if (item.bins.length > 1) {
				const index = item.bins.indexOf(bin);
				item.bins.splice(index, 1);
			}
		}
	}

	setQty(event: any, item: any, bin: any) {
		const inputQty = parseFloat(event.target.value);
		if (isNaN(inputQty)) {
			this.showError = true;
			this.errorMessage = 'Please enter a valid number';
			event.target.value = bin.qty;
			return;
		}

		this.showError = false;

		const currentQty = parseFloat(bin.qty || 0);
		const movement = inputQty - currentQty;
		const newQuantityShipped = this.calculateTotalShipped(item) + movement;

		// Validate against available stock in bin
		if (inputQty > parseFloat(bin.quantity || 0)) {
			if (confirm('You are trying to ship more than available in this bin. Proceed?')) {
				bin.qty = inputQty.toString();
				this.updateItemShippedQuantity(item);
			} else {
				event.target.value = bin.qty;
			}
		} else if (newQuantityShipped > item.quantitypending) {
			if (confirm('You are trying to ship more than pending quantity. Proceed?')) {
				bin.qty = inputQty.toString();
				this.updateItemShippedQuantity(item);
			} else {
				event.target.value = bin.qty;
			}
		} else {
			bin.qty = inputQty.toString();
			this.updateItemShippedQuantity(item);
		}
	}

	selectText(event: any) {
		event.target.select();
	}

	addPick(bin: any, item: any) {
		item.current_movement = 1;
		item.current_bin = bin;
		const currentQty = parseFloat(bin.qty || 0);
		const newQty = currentQty + 1;

		// Validate stock availability
		if (newQty > parseFloat(bin.quantity || 0)) {
			const proceed = confirm('You are trying to ship more than available in this bin. Proceed?');
			if (proceed) {
				bin.qty = newQty.toString();
				this.updateItemShippedQuantity(item);
			} else {
				item.current_movement = 0;
			}
		} else {
			const totalShipped = this.calculateTotalShipped(item);
			if (totalShipped + 1 > item.quantitypending) {
				const proceed = confirm('You are trying to ship more than pending quantity. Proceed?');
				if (proceed) {
					bin.qty = newQty.toString();
					this.updateItemShippedQuantity(item);
				} else {
					item.current_movement = 0;
				}
			} else {
				bin.qty = newQty.toString();
				this.updateItemShippedQuantity(item);
			}
		}
	}

	removePick(bin: any, item: any) {
		item.current_movement = -1;
		item.current_bin = bin;

		let currentQty = parseFloat(bin.qty || 0);
		if (currentQty < 1) {
			currentQty = 0;
			item.current_movement = 0;
			bin.qty = 0;
		} else {
			item.current_movement = -1;
			currentQty = currentQty - 1;
			bin.qty = currentQty.toString();
		}
		this.updateItemShippedQuantity(item);
	}

	setCheckValue(event: any, item: any) {
		let floatvalue = 0;
		if (event.checked) {
			// Set to pending quantity, but don't exceed available stock
			floatvalue = Math.min(item.quantitypending, parseFloat(item.bins[0].quantity || 0));
			item.bins[0].qty = floatvalue;
			item.current_movement = floatvalue;
		} else {
			item.current_movement = -1 * parseFloat(item.bins[0].qty || 0);
			item.bins[0].qty = 0;
		}

		// Remove additional bins if checking all
		if (item.bins.length > 1) {
			item.bins.splice(1, item.bins.length);
		}

		item.bins[0].qty = floatvalue;
		this.updateItemShippedQuantity(item);
	}

	completeShipping() {
		// Validate all items before shipping
		let hasErrors = false;
		for (const item of this.mustship) {
			if (!this.validateStock(item)) {
				hasErrors = true;
				break;
			}
		}

		if (hasErrors) {
			this.globalSearchService.showNotification('Please fix validation errors before shipping', 'danger', 'bottom', 'right');
			return;
		}

		// Build shipping data
		const shippingData = {
			orderno: this.receivingorder,
			items: this.mustship.map((item: any) => {
				const binsToShip = item.bins.filter((bin: any) => parseFloat(bin.qty || 0) > 0);
				return {
					podetailitem: item.podetailitem,
					itemcode: item.itemcode,
					bins: binsToShip.map((bin: any) => ({
						bin: bin.bin,
						loccode: bin.loccode,
						qty: parseFloat(bin.qty || 0)
					}))
				};
			}).filter((item: any) => item.bins.length > 0), // Only include items with bins to ship
			user: this.user
		};

		if (shippingData.items.length === 0) {
			this.globalSearchService.showNotification('No items to ship', 'warning', 'bottom', 'right');
			return;
		}

		this.purchasingService.completeRgaShipping(shippingData).subscribe((result: any) => {
			if (result.success) {
				this.globalSearchService.showNotification(`RGA #${this.receivingorder} shipped successfully`, 'success', 'bottom', 'right');
				this.router.navigate(['/vendors/view/' + this.rgaHeader.supplierno]);
			} else {
				this.globalSearchService.showNotification(result.message || 'Error shipping RGA', 'danger', 'bottom', 'right');
			}
		}, (error) => {
			this.globalSearchService.showNotification('Error shipping RGA', 'danger', 'bottom', 'right');
		});
	}

	/**
	 * Send an RMA inbound notification to PGS 360 for this RGA order.
	 * Sets isReturned=true so PGS knows to receive returned goods.
	 * Only callable when tpl3plPgsEnabled is true.
	 */
	sendToPgs(forceResend = false): void {
		if (!this.tpl3plPgsEnabled || this.pgsSending || !this.receivingorder) { return; }
		this.pgsSending = true;
		this.pgsResult = null;
		this.purchasingService.sendRmaToPgs(String(this.receivingorder), forceResend).subscribe({
			next: (result: any) => {
				this.pgsSending = false;
				this.pgsResult = result;
				this.globalSearchService.showNotification(
					result.success ? 'RMA sent to PGS 360' : ('PGS send failed: ' + (result.message || '')),
					result.success ? 'success' : 'warning',
					'bottom', 'right'
				);
			},
			error: (err: any) => {
				this.pgsSending = false;
				this.pgsResult = { success: false, message: err?.error?.message || 'Request failed' };
				this.globalSearchService.showNotification('PGS send failed', 'warning', 'bottom', 'right');
			}
		});
	}

	back(): void {
		this.location.back();
	}
}
